import { useQuery } from "@tanstack/react-query";

import { listTaskInstancesByNurseryQueryControllerList } from "@/generated/compliance-tasks/compliance-tasks";
import type { ComplianceTaskInstanceResponseDto } from "@/generated/model";
import { useFeatureGate } from "@/hooks/use-feature-gate";
import { FEATURES } from "@/lib/features";

// Re-export under a list-specific name so consumers can swap the source later
// (e.g. add an offline-first cached version) without touching their imports.
export type ComplianceInstanceListItem = ComplianceTaskInstanceResponseDto;

/**
 * Read-side hook that fetches compliance task instances for a nursery, filtered
 * server-side by ZenStack access-control policies — practitioners see only
 * instances assigned to them or their role; managers/admins see all.
 *
 * For now, list view is online-only (TanStack Query). The detail view's
 * `useOfflineComplianceTaskInstance` handles individual-instance offline
 * caching, which is what matters for completion. List-level offline can be
 * added in a follow-up if practitioners report it's needed.
 */
export function useOfflineComplianceInstances(nurseryId: string | null) {
  // Compliance tasks are a Boost+ feature. On the Launch plan the endpoint
  // returns 403, so gate the query on the entitlement rather than firing a
  // request that can only fail (and, before the query-client retry fix, was
  // retried 3× and held the checklists spinner open). While the org context is
  // still resolving, hasFeature is false and the query stays disabled.
  const { hasFeature } = useFeatureGate(FEATURES.COMPLIANCE_TASKS);

  const query = useQuery({
    queryKey: ["compliance-instances-list", nurseryId],
    enabled: !!nurseryId && hasFeature,
    queryFn: async () => {
      if (!nurseryId) return [];
      const response = await listTaskInstancesByNurseryQueryControllerList(
        nurseryId,
        // Pull a generous page so practitioners with many tasks still see them
        // all on the Today list. Pagination only matters for the manager
        // dashboard — that lives elsewhere.
        { limit: 200, offset: 0 },
      );
      // The OpenAPI types this as `void` because the response wrapper isn't
      // captured in the generated client. The actual shape from the BE is
      // `{ data: ComplianceTaskInstanceResponseDto[], meta: PaginationMeta }`
      // — see `PaginatedResponse` in compliance-task application/use-cases/queries/dtos.
      const body = response.data as unknown as
        | { data: ComplianceInstanceListItem[]; meta: { total: number } }
        | undefined;
      return body?.data ?? [];
    },
    // Same staleness as other Today data — refetch on focus so completing one
    // task on another device updates the list within seconds.
    staleTime: 30_000,
  });

  return {
    items: query.data ?? [],
    isLoading: query.isLoading,
    isOffline: !navigator.onLine,
    refetch: query.refetch,
  };
}
